import * as React from 'react';
import { StyleSheet, View, Text, Image, TouchableOpacity } from 'react-native';
import { useLocation } from 'react-router';
import { font } from '../../style/text';
import color from '../../style/color';
import { getLocalizedTexts } from '../../Locales';
import { fetchGiftCardPaymentSessionOrderSummary, GiftCardOrderSummary } from '../../api/rest/giftCardOrderSummary';
import type { Component } from '@checkout.com/checkout-web-components';
import { WalletType, CheckoutMethodType, useLoadCheckoutFlowComponent } from './useGiftCardCheckout';
import { useCyclingText, LottieWithText, getPrePaymentTexts } from './giftCardPaymentUtils';
import { OrderData, formatOrderData } from './giftCardPaymentModel';
import {
    WalletMethodRow,
    GranularConditionsBlock,
    GiftCardPreview,
    OrderSummary,
    PAYMENT_METHOD_LOGOS,
} from './GiftCardPaymentComponents';
import Toggle from '../../components/public/Toggle';
import { mobileOperatingSystem, MobileOperatingSystem } from '../../lib/common/browser';

const logo = { uri: '/assets/images/logos/logo-with-title.svg' };
const chevronLeft = { uri: '/assets/images/icons/chevron-left.svg' };

/**
 * V2 of GiftCardPaymentPage — opened by the new extension at /gift-card-payment-v2.
 * The legacy /gift-card-payment page is kept alive so older extension bundles
 *  continue to work until they update.
 *
 * URL parameters:
 * - paymentSessionToken: The checkout.com payment session token for Flow initialization
 * - paymentSessionPublicToken: Opaque public token used to fetch order display data via the
 *   `gift-card-payment-session/order-summary` REST endpoint (valid 20min, single-use)
 * - platformIssuerId: The platform issuer ID for the gift card
 * - numberOfCards: Number of gift cards in the basket (for singular/plural text)
 * - showTermsAndConditions: 'true' | 'false' — whether to show T&C checkbox (default: false)
 * - region: 'FR' | 'US' — the user's region, used to pick the page's language. The extension
 *   should pass this since the page runs in a signed-out webview (MSE) and would otherwise
 *   fall back to IP/browser-language detection. Consumed by `useRegionFromUrlParameters`
 *   in `RootNavigatorRoutes` before the page renders. Old extension bundles that don't pass
 *   it keep working with today's fallback behavior.
 *
 * The internal `paymentSessionId` is intentionally not exposed to this page: the public lambda
 * does not return it (it encodes the userId), and the extension correlates the save-preference
 * postMessage on `paymentSessionPublicToken` instead.
 *
 * After payment completion, Flow will redirect to the successUrl or failureUrl
 * (the shared `/gift-card-payment/{success,failure}` pages used by both v1 and v2).
 * The extension's background script detects this redirect and sends a message to the contentscript.
 */
function GiftCardPaymentV2Page() {
    const location = useLocation();
    const urlSearchParams = new URLSearchParams(location.search);
    const paymentSessionToken = urlSearchParams.get('paymentSessionToken');
    const paymentSessionPublicToken = urlSearchParams.get('paymentSessionPublicToken');
    const platformIssuerId = urlSearchParams.get('platformIssuerId');
    const numberOfCards = parseInt(urlSearchParams.get('numberOfCards') ?? '1', 10) || 1;
    const showTermsAndConditions = urlSearchParams.get('showTermsAndConditions') === 'true';
    const region = urlSearchParams.get('region');
    const [orderSummaryState, setOrderSummaryState] = React.useState<{
        loading: boolean;
        data?: GiftCardOrderSummary;
        error?: boolean;
        expired?: boolean;
    }>({ loading: !!paymentSessionPublicToken });
    React.useEffect(() => {
        if (!paymentSessionPublicToken) {
            setOrderSummaryState({ loading: false, error: true });
            return;
        }
        let cancelled = false;
        setOrderSummaryState({ loading: true });
        fetchGiftCardPaymentSessionOrderSummary(paymentSessionPublicToken).then((result) => {
            if (cancelled) return;
            if (result.status === 'success') setOrderSummaryState({ loading: false, data: result.data });
            else if (result.status === 'expired') setOrderSummaryState({ loading: false, expired: true });
            else setOrderSummaryState({ loading: false, error: true });
        });
        return () => {
            cancelled = true;
        };
    }, [paymentSessionPublicToken]);
    const orderData: OrderData | null = React.useMemo(
        () => formatOrderData(orderSummaryState.data),
        [orderSummaryState.data]
    );
    // null until checkout.com isAvailable() probing completes after the SDK is initialized
    const [availableWalletMethods, setAvailableWalletMethods] = React.useState<Set<WalletType> | null>(null);
    // null until availability probing completes; then auto-set to best available method
    const [selectedMethod, setSelectedMethod] = React.useState<CheckoutMethodType | null>(null);
    const sharedParams = new URLSearchParams({
        platformIssuerId: platformIssuerId ?? '',
        ...(region ? { region } : {}),
    });
    const failureUrl = `/gift-card-payment/failure?${sharedParams}`;
    const successUrl = `/gift-card-payment/success?${new URLSearchParams({
        ...Object.fromEntries(sharedParams),
        numberOfCards: String(numberOfCards),
        paymentMethodType: selectedMethod ?? 'payment_card',
    })}`;
    const [status, setStatus] = React.useState<'loading' | 'ready' | 'error'>('loading');
    const [isSavedForFuturePurchases, setIsSavedForFuturePurchases] = React.useState(false);
    const [termsAccepted, setTermsAccepted] = React.useState(false);
    const checkoutComponentRef = React.useRef<Component | null>(null);
    const handleWalletAvailabilityProbed = React.useCallback((methods: Set<WalletType>) => {
        setAvailableWalletMethods(methods);
        setSelectedMethod((current) => {
            if (current === null || current === 'payment_card') return 'payment_card';
            return methods.has(current as WalletType) ? current : 'payment_card';
        });
    }, []);
    useLoadCheckoutFlowComponent(
        paymentSessionToken,
        setStatus,
        successUrl,
        failureUrl,
        orderSummaryState.error ? null : selectedMethod,
        checkoutComponentRef,
        handleWalletAvailabilityProbed
    );
    const texts = getLocalizedTexts().giftCardPaymentPage;
    const prePaymentTexts = React.useMemo(() => getPrePaymentTexts(numberOfCards), [numberOfCards]);
    const loadingText = useCyclingText(prePaymentTexts);

    // Auto-redirect to failure URL when Checkout.com form fails to load.
    // The extension background script detects `reason=sdkLoadFailed` on the failure URL and
    // emits the `checkoutDotComPaymentFailed` analytics event from its authenticated context.
    React.useEffect(() => {
        if (status !== 'error') return;
        const timer = setTimeout(() => {
            window.location.href = `${failureUrl}&reason=sdkLoadFailed`;
        }, 5000);
        return () => clearTimeout(timer);
    }, [status, failureUrl]);

    const handleBack = React.useCallback(() => {
        window.location.href = `${failureUrl}&reason=cancelled`;
    }, [failureUrl]);

    const handleSelectMethod = React.useCallback(
        (method: CheckoutMethodType) => {
            if (method === selectedMethod) return;
            setSelectedMethod(method);
            setTermsAccepted(false);
        },
        [selectedMethod]
    );

    const handleToggleSaveCard = React.useCallback(
        (value: boolean) => {
            setIsSavedForFuturePurchases(value);
            window.postMessage(
                { type: 'joko:giftCardSavePreference', shouldSave: value, paymentSessionPublicToken },
                window.location.origin
            );
        },
        [paymentSessionPublicToken]
    );

    // iOS Safari auto-zooms on focus into the checkout.com card inputs and the zoom
    // doesn't reset across the redirect to the 3D-Secure / success / failure page.
    // Setting maximum-scale=1 on this page's viewport prevents the auto-zoom
    // from being triggered in the first place. Scoped to iOS so we don't block
    // pinch-zoom on other platforms. Restored on unmount so the rest of the SPA
    // keeps its default viewport.
    React.useEffect(() => {
        if (mobileOperatingSystem !== MobileOperatingSystem.iOS) return;
        const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
        if (!viewport) return;
        const original = viewport.content;
        viewport.content = 'width=device-width, initial-scale=1, maximum-scale=1';
        return () => {
            viewport.content = original;
        };
    }, []);

    const showSaveCardToggle = selectedMethod === 'payment_card' && status === 'ready';
    const canPay = !showTermsAndConditions || termsAccepted;

    if (orderSummaryState.expired)
        return (
            <View style={styles.page}>
                <ResponsivePagePaddingStyle />
                <View style={styles.header}>
                    <Image source={logo} style={styles.headerLogo} />
                </View>
                <View style={[styles.content, { justifyContent: 'center', alignItems: 'center' }]}>
                    <Text style={styles.pageHeading}>{texts.sessionExpiredTitle}</Text>
                    <Text style={styles.pageSubtitle}>{texts.sessionExpiredSubtitle}</Text>
                </View>
            </View>
        );

    if (orderSummaryState.error || (!orderSummaryState.loading && !orderData))
        return (
            <View style={styles.page}>
                <ResponsivePagePaddingStyle />
                <View style={styles.header}>
                    <Image source={logo} style={styles.headerLogo} />
                </View>
                <View style={[styles.content, { justifyContent: 'center', alignItems: 'center' }]}>
                    <Text style={styles.pageHeading}>{texts.paymentError}</Text>
                    <Text style={styles.pageSubtitle}>{texts.orderDataErrorSubtitle}</Text>
                </View>
            </View>
        );

    return (
        <View style={styles.page}>
            <ResponsivePagePaddingStyle />
            <View style={styles.header}>
                <Image source={logo} style={styles.headerLogo} />
            </View>

            <View style={styles.content}>
                <TouchableOpacity style={styles.backLink} onPress={handleBack}>
                    <Image source={chevronLeft} style={styles.backIcon} />
                    <Text style={styles.backText}>{texts.back}</Text>
                </TouchableOpacity>

                {status === 'error' && (
                    <View style={[styles.messageContainer, styles.messageContainerMobile]}>
                        <Text style={styles.pageHeading}>{texts.paymentError}</Text>
                        <Text style={styles.pageSubtitle}>{texts.paymentFormErrorSubtitle}</Text>
                    </View>
                )}
                {status === 'ready' && (
                    <View style={[styles.messageContainer, styles.messageContainerMobile]}>
                        <Text style={styles.pageHeading}>{texts.secureCheckoutTitle}</Text>
                        <Text style={styles.pageSubtitle}>{texts.secureCheckoutSubtitle}</Text>
                    </View>
                )}

                <View style={styles.body}>
                    <View style={styles.leftColumn}>
                        {status === 'loading' && <LottieWithText text={loadingText} />}
                        {status === 'error' && (
                            <View style={[styles.messageContainer, styles.messageContainerDesktop]}>
                                <Text style={styles.pageHeading}>{texts.paymentError}</Text>
                                <Text style={styles.pageSubtitle}>{texts.paymentFormErrorSubtitle}</Text>
                            </View>
                        )}
                        {status === 'ready' && (
                            <View style={[styles.messageContainer, styles.messageContainerDesktop]}>
                                <Text style={styles.pageHeading}>{texts.secureCheckoutTitle}</Text>
                                <Text style={styles.pageSubtitle}>{texts.secureCheckoutSubtitle}</Text>
                            </View>
                        )}

                        {/* Payment method picker — shown when ready; wallet rows filtered by checkout.com isAvailable() */}
                        {status === 'ready' && (
                            <View style={styles.walletMethodsBox}>
                                <Text style={styles.walletMethodsTitle}>{texts.paymentMethods}</Text>
                                <WalletMethodRow
                                    logo={PAYMENT_METHOD_LOGOS.creditCardLogo}
                                    label={texts.creditCard}
                                    selected={selectedMethod === 'payment_card'}
                                    onPress={() => handleSelectMethod('payment_card')}
                                    hasBorderBottom={
                                        availableWalletMethods?.has('apple_pay') || availableWalletMethods?.has('google_pay')
                                    }
                                />
                                {availableWalletMethods?.has('apple_pay') && (
                                    <WalletMethodRow
                                        logo={PAYMENT_METHOD_LOGOS.applePayLogo}
                                        label="Apple Pay"
                                        selected={selectedMethod === 'apple_pay'}
                                        onPress={() => handleSelectMethod('apple_pay')}
                                        hasBorderBottom={availableWalletMethods?.has('google_pay')}
                                    />
                                )}
                                {availableWalletMethods?.has('google_pay') && (
                                    <WalletMethodRow
                                        logo={PAYMENT_METHOD_LOGOS.googlePayLogo}
                                        label="Google Pay"
                                        selected={selectedMethod === 'google_pay'}
                                        onPress={() => handleSelectMethod('google_pay')}
                                        hasBorderBottom={false}
                                    />
                                )}
                            </View>
                        )}

                        {/* Card form box — always in DOM (opacity:0 when inactive) so the SDK can initialize;
                         * wallet methods use a separate container below the checkbox instead. */}
                        <View
                            style={[
                                styles.cardFormBox,
                                selectedMethod !== 'payment_card' ? styles.cardFormBoxHidden : undefined,
                                status !== 'ready' ? styles.cardFormBoxHidden : undefined,
                            ]}>
                            <div id="checkout-card-container" />
                            {showSaveCardToggle && (
                                <View style={styles.saveCardRow}>
                                    <Text style={styles.saveCardLabel}>{texts.saveForNextPurchases}</Text>
                                    <Toggle value={isSavedForFuturePurchases} onValueChange={handleToggleSaveCard} />
                                </View>
                            )}
                        </View>

                        {/* Terms and pay button */}
                        <View
                            style={status !== 'ready' ? { opacity: 0 } : undefined}
                            pointerEvents={status !== 'ready' ? 'none' : 'auto'}>
                            {showTermsAndConditions &&
                                (orderSummaryState.loading ? (
                                    <ConditionsSkeleton />
                                ) : orderData?.granularConditionsOfUse ? (
                                    <GranularConditionsBlock
                                        granularConditionsOfUse={orderData.granularConditionsOfUse}
                                        conditionsOfUseUrl={orderData.conditionsOfUseUrl}
                                    />
                                ) : null)}
                            {showTermsAndConditions && (
                                <TouchableOpacity
                                    style={styles.termsRow}
                                    onPress={() => setTermsAccepted((v) => !v)}
                                    activeOpacity={0.7}>
                                    <View style={[styles.termsRadio, termsAccepted && styles.termsRadioChecked]}>
                                        {termsAccepted && (
                                            <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                                                <path
                                                    d="M1 4L4.5 7.5L11 1"
                                                    stroke="white"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        )}
                                    </View>
                                    <Text style={styles.termsText}>
                                        {`${texts.tos.part1} `}
                                        <Text
                                            style={styles.termsLink}
                                            onPress={() => window.open(texts.tos.tosUrl, '_blank', 'noopener,noreferrer')}>
                                            {texts.tos.tos}
                                        </Text>
                                        {` ${texts.tos.part2} `}
                                        <Text
                                            style={styles.termsLink}
                                            onPress={() =>
                                                window.open(texts.tos.privacyPolicyUrl, '_blank', 'noopener,noreferrer')
                                            }>
                                            {texts.tos.privacyPolicy}
                                        </Text>
                                    </Text>
                                </TouchableOpacity>
                            )}
                            <View style={styles.payButtonWrapper}>
                                {selectedMethod === 'payment_card' ? (
                                    <TouchableOpacity
                                        style={[styles.validateButton, !canPay && styles.validateButtonDisabled]}
                                        onPress={() => checkoutComponentRef.current?.submit()}
                                        disabled={!canPay}
                                        activeOpacity={0.8}>
                                        <Text
                                            style={[
                                                styles.validateButtonText,
                                                !canPay && styles.validateButtonTextDisabled,
                                            ]}>
                                            {texts.validatePayment}
                                        </Text>
                                    </TouchableOpacity>
                                ) : (
                                    /* SDK wallet button — checkout.com renders directly into this container */
                                    <div style={{ position: 'relative', width: '100%' }}>
                                        <div id="checkout-flow-container" style={!canPay ? { opacity: 0.4 } : undefined} />
                                        {!canPay && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    cursor: 'not-allowed',
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </View>
                        </View>
                    </View>

                    <View style={styles.rightColumn}>
                        {orderSummaryState.loading ? (
                            <OrderDataSkeleton />
                        ) : orderData ? (
                            <>
                                <GiftCardPreview orderData={orderData} numberOfCards={numberOfCards} />
                                <OrderSummary orderData={orderData} />
                            </>
                        ) : null}
                    </View>
                </View>
            </View>
        </View>
    );
}

export default GiftCardPaymentV2Page;

function ResponsivePagePaddingStyle() {
    return (
        <style>{`
            :root {
                --gc-page-pad: 40px;
                --gc-body-dir: row;
                --gc-body-gap: 80px;
                --gc-right-col-width: 375px;
                --gc-heading-mobile-display: none;
                --gc-heading-desktop-display: flex;
            }
            @media (max-width: 767px) {
                :root {
                    --gc-page-pad: 16px;
                    --gc-body-dir: column-reverse;
                    --gc-body-gap: 24px;
                    --gc-right-col-width: 100%;
                    --gc-heading-mobile-display: flex;
                    --gc-heading-desktop-display: none;
                }
            }
            @media (min-width: 1025px) { :root { --gc-page-pad: 60px; } }
            @media (min-width: 1201px) { :root { --gc-page-pad: 180px; } }
        `}</style>
    );
}

const styles = StyleSheet.create({
    page: {
        minHeight: '100vh',
        backgroundColor: '#f7f8fa',
    },

    header: {
        paddingLeft: 'var(--gc-page-pad)' as unknown as number,
        paddingRight: 'var(--gc-page-pad)' as unknown as number,
        paddingVertical: 19,
        borderBottomWidth: 1,
        borderBottomColor: '#e3e3e3',
        backgroundColor: color.white,
    },
    headerLogo: {
        width: 114,
        height: 55,
        resizeMode: 'contain',
    },

    content: {
        paddingLeft: 'var(--gc-page-pad)' as unknown as number,
        paddingRight: 'var(--gc-page-pad)' as unknown as number,
        paddingTop: 16,
        paddingBottom: 40,
    },

    backLink: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 32,
    },
    backIcon: {
        width: 17,
        height: 17,
        resizeMode: 'contain',
    },
    backText: {
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 14,
        lineHeight: 17,
        color: color.black,
        textDecorationLine: 'underline',
    },

    body: {
        flexDirection: 'var(--gc-body-dir)' as unknown as 'row',
        gap: 'var(--gc-body-gap)' as unknown as number,
    },

    leftColumn: {
        flex: 1,
    },

    rightColumn: {
        width: 'var(--gc-right-col-width)' as unknown as number,
        gap: 16,
    },

    messageContainer: {
        marginBottom: 16,
    },
    messageContainerMobile: {
        display: 'var(--gc-heading-mobile-display)' as unknown as 'flex',
    },
    messageContainerDesktop: {
        display: 'var(--gc-heading-desktop-display)' as unknown as 'flex',
    },
    pageHeading: {
        fontFamily: font.wallopSansBold,
        fontSize: 24,
        lineHeight: 29,
        letterSpacing: -0.48,
        color: color.black,
    },
    pageSubtitle: {
        fontFamily: font.instrumentSansMedium,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
        marginTop: 4,
    },

    walletMethodsBox: {
        width: '100%',
        backgroundColor: color.white,
        borderWidth: 1,
        borderColor: '#E3E3E3',
        borderRadius: 16,
        marginBottom: 16,
        overflow: 'hidden',
        paddingHorizontal: 16,
    },
    walletMethodsTitle: {
        fontFamily: font.instrumentSansBold,
        fontSize: 16,
        lineHeight: 24,
        color: color.black,
        paddingTop: 16,
        paddingBottom: 12,
    },

    cardFormBox: {
        width: '100%',
        backgroundColor: color.white,
        borderWidth: 1,
        borderColor: '#E3E3E3',
        borderRadius: 16,
        padding: 16,
        marginBottom: 16,
    },
    cardFormBoxHidden: {
        opacity: 0,
        pointerEvents: 'none' as const,
        height: 0,
        overflow: 'hidden',
        padding: 0,
        marginBottom: 0,
        borderWidth: 0,
    },

    saveCardRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 16,
        gap: 16,
    },
    saveCardLabel: {
        fontFamily: font.instrumentSansRegular,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },
    termsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        minHeight: 48,
        paddingVertical: 8,
        marginBottom: 16,
    },
    termsRadio: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 1.5,
        borderColor: color.black,
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    termsRadioChecked: {
        backgroundColor: color.positiveGreen,
        borderColor: color.positiveGreen,
    },
    termsText: {
        fontFamily: font.instrumentSansMedium,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
        flex: 1,
    },
    termsLink: {
        textDecorationLine: 'underline',
    },
    payButtonWrapper: {
        position: 'relative',
        width: '100%',
    },
    validateButton: {
        backgroundColor: color.black,
        borderRadius: 96,
        height: 44,
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    validateButtonDisabled: {
        backgroundColor: '#c8c8c8',
    },
    validateButtonText: {
        fontFamily: font.instrumentSansMedium,
        fontSize: 16,
        lineHeight: 19,
        color: color.white,
        textAlign: 'center',
    },
    validateButtonTextDisabled: {
        color: '#f1f1f1',
    },
});

function ConditionsSkeleton() {
    return (
        <div
            style={{
                width: '100%',
                border: '1px solid #000',
                borderRadius: 16,
                marginBottom: 8,
                overflow: 'hidden',
                padding: '16px 16px 0',
            }}>
            <div className="skeleton-box" style={{ height: 24, width: '55%', marginBottom: 16 }} />
            {[70, 85, 60].map((width, i) => (
                <div
                    key={i}
                    style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'center',
                        paddingBottom: 12,
                        borderBottom: i < 2 ? '1px solid #E3E3E3' : 'none',
                        marginBottom: 12,
                    }}>
                    <div className="skeleton-box" style={{ width: 20, height: 20, flexShrink: 0 }} />
                    <div className="skeleton-box" style={{ height: 16, width: `${width}%` }} />
                </div>
            ))}
        </div>
    );
}

function OrderDataSkeleton() {
    return (
        <>
            <style>{`
                @keyframes shimmer {
                    0% { background-position: -600px 0; }
                    100% { background-position: 600px 0; }
                }
                .skeleton-box {
                    background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
                    background-size: 600px 100%;
                    animation: shimmer 1.4s infinite linear;
                    border-radius: 12px;
                }
            `}</style>
            {/* Card preview skeleton */}
            <div className="skeleton-box" style={{ width: '100%', height: 200, borderRadius: 16 }} />
            {/* Order summary skeleton */}
            <div
                style={{
                    backgroundColor: '#fff',
                    borderRadius: 16,
                    padding: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                }}>
                <div className="skeleton-box" style={{ height: 20, width: '40%' }} />
                <div className="skeleton-box" style={{ height: 16, width: '70%' }} />
                <div className="skeleton-box" style={{ height: 16, width: '55%' }} />
                <div style={{ borderTopWidth: 1, borderTopColor: '#e3e3e3', borderTopStyle: 'solid', paddingTop: 12 }}>
                    <div className="skeleton-box" style={{ height: 20, width: '50%' }} />
                </div>
            </div>
        </>
    );
}
